import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,

  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ViewList as ViewListIcon,
  ViewColumn as ViewColumnIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as OpportunitiesIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
// Using standard HTML date inputs instead of MUI DatePicker for compatibility
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { opportunityService } from '../services/opportunityService';
import { companyService } from '../services/companyService';
import { lobService } from '../services/lobService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import userService from '../services/userService';
import { useUserData } from '../hooks/useUserData';
import { createPortal } from 'react-dom';
import { DEAL_STAGES, CLOSED_STAGES } from '../constants/dealStages';
import UserDisplay from '../components/UserDisplay';
import { v4 as uuidv4 } from 'uuid';
import useAdminStatus from '../hooks/useAdminStatus';
import salesPersonService from '../services/salesPersonService';
import { logError, formatErrorMessage } from '../utils/errorLogger';

const SWIM_LANES = {
  BACKLOG: 'Backlog',
  LEAD: 'Lead',
  QUALIFIED: 'Qualified',
  PROPOSED: 'Proposed',
  CLOSED: 'Closed'
};

// Hook to fix draggable disappearing in portals/fixed containers
const useDraggableInPortal = () => {
  const self = useRef({}).current;

  useEffect(() => {
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.pointerEvents = 'none';
    div.style.top = '0';
    div.style.width = '100%';
    div.style.height = '100%';
    div.style.zIndex = '9999';
    self.elt = div;
    document.body.appendChild(div);
    return () => {
      document.body.removeChild(div);
    };
  }, [self]);

  return (render) => (provided, ...args) => {
    const element = render(provided, ...args);
    if (provided.draggableProps.style?.position === 'fixed') {
      return createPortal(element, self.elt);
    }
    return element;
  };
};

const Opportunities = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentUser = useCurrentUser();
  const { isAdmin } = useAdminStatus();
  const { preloadUserData, getCachedUserData } = useUserData();

  // State management
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companies, setCompanies] = useState([]);
  const [lobOptions, setLobOptions] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [userDisplayData, setUserDisplayData] = useState({});
  const [viewMode, setViewMode] = useState(isMobile ? 'list' : 'swimlane');
  const [selectedStages, setSelectedStages] = useState([
    DEAL_STAGES.BACKLOG,
    DEAL_STAGES.LEAD,
    DEAL_STAGES.QUALIFIED,
    DEAL_STAGES.PROPOSED,
    DEAL_STAGES.CLOSED_WON,
    DEAL_STAGES.CLOSED_LOST,
    DEAL_STAGES.CANCELLED
  ]);
  const [selectedLOBs, setSelectedLOBs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('opportunityName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [timeRangeEnabled, setTimeRangeEnabled] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [collapsedLanes, setCollapsedLanes] = useState(new Set(['BACKLOG']));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeData, setCloseData] = useState({});
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Hook for portal-based dragging to fix disappearing cards
  const renderDraggable = useDraggableInPortal();

  // Update view mode when screen size changes
  useEffect(() => {
    if (isMobile) {
      setViewMode('list');
    }
  }, [isMobile]);

  // Convert opportunity format for display
  const convertOpportunityFormat = useCallback((opportunity) => {
    const converted = {
      ...opportunity,
      // Ensure arrays are initialized
      salesPersonEmails: opportunity.salesPersonEmails || [],
      contactIds: opportunity.contactIds || [],
      comments: opportunity.comments || [],
      moments: opportunity.moments || []
    };

    // Handle FirstSalespersonEmail if available
    if (opportunity.FirstSalespersonEmail) {
      converted.salesPersonEmails = [opportunity.FirstSalespersonEmail];
      // Preserve PascalCase fields
      converted.FirstSalespersonEmail = opportunity.FirstSalespersonEmail;
      converted.FirstSalespersonName = opportunity.FirstSalespersonName;
      converted.FirstSalespersonInitials = opportunity.FirstSalespersonInitials;
      // Update global userDisplayData state
      setUserDisplayData(prev => ({
        ...prev,
        [opportunity.FirstSalespersonEmail]: {
          name: opportunity.FirstSalespersonName || opportunity.FirstSalespersonEmail.split('@')[0],
          initials: opportunity.FirstSalespersonInitials || opportunity.FirstSalespersonEmail.charAt(0).toUpperCase(),
          email: opportunity.FirstSalespersonEmail
        }
      }));
    }

    // Convert salesPersons array to salesPersonEmails array (legacy support)
    if (opportunity.salesPersons && Array.isArray(opportunity.salesPersons)) {
      if (opportunity.salesPersons.length > 0 && typeof opportunity.salesPersons[0] === 'object') {
        // Old format: array of objects
        converted.salesPersonEmails = opportunity.salesPersons.map(sp => sp.email || sp.id).filter(email => email);
        delete converted.salesPersons;
      } else if (opportunity.salesPersons.length > 0 && typeof opportunity.salesPersons[0] === 'string') {
        // Already in email format
        converted.salesPersonEmails = opportunity.salesPersons;
        delete converted.salesPersons;
      }
    }
    
    // Convert createdBy to createdByEmail
    if (opportunity.createdBy && !opportunity.createdByEmail) {
      converted.createdByEmail = opportunity.createdBy;
      delete converted.createdBy;
      delete converted.createdByName;
      delete converted.createdByPhoto;
    }
    
    return converted;
  }, []); // No dependencies needed as it only uses stable references

  // Load user display data for all opportunities
  const loadUserDisplayData = useCallback(async (opportunities) => {
    try {
      const emails = new Set();
      
      // Collect all emails from opportunities
      opportunities.forEach(opp => {
        if (opp.createdByEmail) emails.add(opp.createdByEmail);
        if (opp.salesPersonEmails && opp.salesPersonEmails.length > 0) {
          opp.salesPersonEmails.forEach(email => emails.add(email));
        }
      });
      
      // Use centralized user data cache
      await preloadUserData([...emails]);
      
      // Create display data from salespersons and cached user data
      const displayData = {};
      for (const email of emails) {
        try {
          // First check if user exists in salesPersons
          const existingSalesperson = salesPersons.find(sp => (sp.Email || sp.email)?.toLowerCase() === email.toLowerCase());
          
          if (existingSalesperson) {
            displayData[email] = {
              name: existingSalesperson.Name || existingSalesperson.name,
              initials: existingSalesperson.Initials || existingSalesperson.initials,
              email: email,
              photoUrl: null // Will be fetched by UserDisplay component using centralized cache
            };
          } else {
            // Use fallback to email-based display
            displayData[email] = {
              name: email.split('@')[0],
              initials: email[0].toUpperCase(),
              email: email,
              photoUrl: null
            };
          }
        } catch (err) {
          console.error('Error loading user display data for', email, err);
          // Use fallback display data
          displayData[email] = {
            name: email.split('@')[0],
            initials: email[0].toUpperCase(),
            email: email,
            photoUrl: null
          };
        }
      }
      
      setUserDisplayData(displayData);
    } catch (err) {
      console.error('Error in loadUserDisplayData:', err);
      setError(prev => prev + '\nFailed to load user display data: ' + err.message);
    }
  }, [salesPersons, preloadUserData]);

  // Load data
  const loadData = useCallback(async (forceRefresh = false) => {
    if (loading && !forceRefresh) return; // Prevent concurrent loads unless forced
    setLoading(true);
    setError('');

    try {
      // Load all reference data first
      const [companiesData, lobData, salesPersonsData] = await Promise.all([
        companyService.getCompanies().catch(err => {
          setError(prev => prev + '\nFailed to load companies: ' + err.message);
          return [];
        }),
        lobService.getLOBOptions().catch(err => {
          setError(prev => prev + '\nFailed to load LOB options: ' + err.message);
          return [];
        }),
        salesPersonService.getSalesPersons().catch(err => {
          setError(prev => prev + '\nFailed to load salespersons: ' + err.message);
          return [];
        })
      ]);

      setCompanies(companiesData);
      setLobOptions(lobData);
      setSalesPersons(salesPersonsData);
      
      const [opportunitiesData] = await Promise.all([
        opportunityService.getOpportunities()
      ]);

      // Convert opportunities to new format if needed
      const convertedOpportunities = await Promise.all(
        (opportunitiesData || []).map(async (opp) => {
          const converted = await convertOpportunityFormat(opp);
          return converted;
        })
      );

      setOpportunities(convertedOpportunities);

      // Load user display data for all opportunities
      await loadUserDisplayData(convertedOpportunities);

      // Clear any errors on successful load
      setError('');
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [convertOpportunityFormat, loadUserDisplayData, salesPersons]);

  // Initialize selected LOBs when LOB options are loaded
  useEffect(() => {
    // Only initialize if we have LOB options and no URL parameter for LOB
    if (lobOptions.length > 0 && selectedLOBs.length === 0) {
      const searchParams = new URLSearchParams(location.search);
      const lobParam = searchParams.get('lob');
      
      if (lobParam) {
        // If we have a LOB parameter, only select that one
        setSelectedLOBs([lobParam]);
      }
      // Otherwise, leave the selection empty
    }
  }, [lobOptions, selectedLOBs.length, location.search]);

  // Handle URL parameters for filtering (from dashboard pie chart clicks)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const lobParam = searchParams.get('lob');
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const timeRangeEnabledParam = searchParams.get('timeRangeEnabled');

    // Only switch to list view if there are actual filter parameters (from pie chart clicks)
    let hasFilterParams = false;

    if (lobParam && lobOptions.length > 0) {
      // Set LOB filter to only the selected LOB
      setSelectedLOBs([lobParam]);
      hasFilterParams = true;
    }

    if (timeRangeEnabledParam === 'true' && startDateParam && endDateParam) {
      // Enable time range filter and set dates
      setTimeRangeEnabled(true);
      setStartDate(startDateParam);
      setEndDate(endDateParam);
      hasFilterParams = true;
    }

    // Only switch to list view if there are filter parameters (from pie chart clicks)
    if (hasFilterParams) {
      setViewMode('list');
    }
  }, [location.search, lobOptions]);

  // Handle stage parameter separately to avoid dependency issues
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const stageParam = searchParams.get('stage');

    if (stageParam) {
      // Set stage filter to only the selected stage
      setSelectedStages([stageParam]);
      // Switch to list view when stage parameter is present (from pie chart clicks)
      setViewMode('list');
    }
  }, [location.search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh functionality
  useEffect(() => {
    // Refresh data when window regains focus (user switches back to tab)
    const handleFocus = () => {
      // Clear user cache to ensure fresh user data
      userService.clearCache();
      loadData(false); // Don't show loading indicator for background refresh
    };

    // Refresh data every 2 minutes when tab is active
    const refreshInterval = setInterval(() => {
      if (!document.hidden) {
        loadData(false); // Don't show loading indicator for background refresh
      }
    }, 2 * 60 * 1000); // 2 minutes

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(refreshInterval);
    };
  }, [loadData]);

  // Helper functions

  const getFirstSalesPerson = (salesPersonEmails) => {
    if (!salesPersonEmails || salesPersonEmails.length === 0) {
      return null;
    }
    
    const email = salesPersonEmails[0];
    const userInfo = userDisplayData[email];
    
    return userInfo ? {
      email: email,
      name: userInfo.name,
      photoUrl: userInfo.photoUrl,
      initials: userInfo.initials
    } : null;
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Format currency for display in input fields (with $ and commas)
  const formatCurrencyForDisplay = (value) => {
    if (value === '' || value === null || value === undefined || isNaN(value)) return '';
    const numValue = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  // Parse currency input (remove $ and commas, return number)
  const parseCurrencyInput = (value) => {
    if (!value || value === '') return '';
    // Remove $ and commas, keep only numbers and decimal point
    const cleanValue = value.toString().replace(/[$,]/g, '');
    return cleanValue;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Split off time part and use noon to avoid timezone issues
    const [datePart] = dateString.split('T');
    const date = new Date(datePart + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
  };

  // Compute filtered opportunities
  const filteredOpportunities = React.useMemo(() => {
    return opportunities
      .filter(opportunity => {
        // Stage filter
        const matchesStage = selectedStages.includes(opportunity.dealStage) ||
          selectedStages.includes(opportunity.DealStage);
        
        // LOB filter
        const matchesLOB = selectedLOBs.length === 0 || 
          selectedLOBs.includes(opportunity.lineOfBusinessName) ||
          selectedLOBs.includes(opportunity.LineOfBusinessName) ||
          selectedLOBs.includes(opportunity.lineOfBusiness);

        // Time range filter
        let matchesTimeRange = true;
        if (timeRangeEnabled && (startDate || endDate)) {
          const opportunityDate = new Date(
            CLOSED_STAGES.includes(opportunity.dealStage) 
              ? opportunity.closedDate 
              : opportunity.estimatedCloseDate
          );
          
          if (startDate) {
            matchesTimeRange = matchesTimeRange && opportunityDate >= new Date(startDate);
          }
          if (endDate) {
            matchesTimeRange = matchesTimeRange && opportunityDate <= new Date(endDate);
          }
        }
        
        // Search text
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm || 
          (opportunity.opportunityName || '').toLowerCase().includes(searchLower) ||
          (opportunity.companyName || '').toLowerCase().includes(searchLower) ||
          (opportunity.lineOfBusinessName || opportunity.LineOfBusinessName || '').toLowerCase().includes(searchLower);

        return matchesStage && matchesLOB && matchesTimeRange && matchesSearch;
      })
      .sort((a, b) => {
        // Sort by the selected field
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        // Special handling for different fields
        switch (sortField) {
          case 'name':
            aValue = a.opportunityName || '';
            bValue = b.opportunityName || '';
            break;
          case 'company':
            aValue = a.companyName || '';
            bValue = b.companyName || '';
            break;
          case 'value':
            aValue = CLOSED_STAGES.includes(a.dealStage) ? (a.closedValue || 0) : (a.estimatedValue || 0);
            bValue = CLOSED_STAGES.includes(b.dealStage) ? (b.closedValue || 0) : (b.estimatedValue || 0);
            break;
          case 'date':
            aValue = new Date(CLOSED_STAGES.includes(a.dealStage) ? a.closedDate : a.estimatedCloseDate);
            bValue = new Date(CLOSED_STAGES.includes(b.dealStage) ? b.closedDate : b.estimatedCloseDate);
            break;
          default:
            break;
        }
        
        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';
        
        // Compare based on sort direction
        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
        }
      });
  }, [opportunities, selectedStages, selectedLOBs, timeRangeEnabled, startDate, endDate, searchTerm, sortField, sortDirection]);

  // Helper function to render sortable table header
  const renderSortableHeader = (field, label) => {
    const isActive = sortField === field;
    const direction = isActive ? sortDirection : 'asc';
    
    return (
      <TableCell
        sx={{ 
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
        }}
        onClick={() => handleSort(field)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {label}
          {isActive ? (
            direction === 'asc' ? (
              <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} />
            ) : (
              <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
            )
          ) : (
            <Box sx={{ ml: 1, width: 16 }} /> // Placeholder for alignment
          )}
        </Box>
      </TableCell>
    );
  };

  // Handle stage filter changes
  const handleStageFilterChange = (event) => {
    const value = event.target.value;
    
    // Handle "All" selection
    if (value.includes('All')) {
      if (selectedStages.length === Object.values(DEAL_STAGES).length) {
        // If all are selected, deselect all
        setSelectedStages([]);
      } else {
        // If not all are selected, select all
        setSelectedStages(Object.values(DEAL_STAGES));
      }
    } else {
      setSelectedStages(value);
    }
    setPage(0); // Reset to first page when filtering
  };

  // Handle LOB filter changes
  const handleLOBFilterChange = (event) => {
    const value = event.target.value;
    
    // Handle "All" selection
    if (value.includes('All')) {
      // If all are currently selected, clear the selection
      if (selectedLOBs.length === lobOptions.length) {
        setSelectedLOBs([]);
      } else {
        // Otherwise, select all
        setSelectedLOBs(lobOptions.map(lob => lob.Name || lob.name));
      }
    } else {
      // For normal selection, just use the selected values
      setSelectedLOBs(value);
    }
    setPage(0); // Reset to first page when filtering
  };

  // Handle time range toggle
  const handleTimeRangeToggle = (event) => {
    setTimeRangeEnabled(event.target.checked);
    if (!event.target.checked) {
      // Clear dates when disabled
      setStartDate('');
      setEndDate('');
    }
    setPage(0); // Reset to first page when filtering
  };

  // Handle date changes
  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setSortField(field);
      setSortDirection(field === 'date' ? 'desc' : 'asc'); // Date defaults to desc, others to asc
    }
    setPage(0); // Reset to first page when sorting
  };

  // Handle drag and drop for swim lanes
  const handleDragEnd = async (result) => {
    
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    
    // If dropped in the same position, do nothing
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const opportunityId = draggableId;
    const newStage = getStageFromSwimLane(destination.droppableId);
    const opportunity = opportunities.find(opp => opp.opportunityId === opportunityId);
    
    if (!opportunity || opportunity.dealStage === newStage) return;

    // Check if moving to closed stage
    const isMovingToClosed = destination.droppableId === 'CLOSED' && 
                            !CLOSED_STAGES.includes(opportunity.dealStage);

    if (isMovingToClosed) {
      // Show close dialog
      setCloseData({
        opportunityId,
        stage: DEAL_STAGES.CLOSED_WON, // Default to won
        value: opportunity.estimatedValue || '',
        date: new Date(),
        reason: ''
      });
      setCloseDialogOpen(true);
    } else {
      // Update stage directly
      await updateOpportunityStage(opportunityId, newStage);
    }
  };

  // Get stage from swim lane
  const getStageFromSwimLane = (swimLaneId) => {
    switch (swimLaneId) {
      case 'BACKLOG': return DEAL_STAGES.BACKLOG;
      case 'LEAD': return DEAL_STAGES.LEAD;
      case 'QUALIFIED': return DEAL_STAGES.QUALIFIED;
      case 'PROPOSED': return DEAL_STAGES.PROPOSED;
      case 'CLOSED': return DEAL_STAGES.CLOSED_WON; // Default closed stage
      default: return DEAL_STAGES.LEAD;
    }
  };

  // Get swim lane from stage
  const getSwimLaneFromStage = (stage) => {
    if (!stage) {
      return 'BACKLOG'; // Default to BACKLOG if stage is undefined/null
    }
    
    if (CLOSED_STAGES.includes(stage)) {
      return 'CLOSED';
    }
    
    return stage.toUpperCase().replace(' ', '_');
  };

  // Update opportunity stage
  const updateOpportunityStage = async (opportunityId, newStage, closeData = null) => {
    try {
      // Find the opportunity to update
      const opportunity = opportunities.find(opp => opp.opportunityId === opportunityId);
      if (!opportunity) {
        throw new Error('Opportunity not found');
      }

      const oldStage = opportunity.dealStage;
      
      // Create a copy of the opportunity with the new stage
      const updatedOpportunity = {
        ...opportunity,
        dealStage: newStage,
        modifiedByEmail: currentUser.email,
        modifiedDate: new Date().toISOString()
      };

      // If this is a close action, add the close data
      if (closeData) {
        updatedOpportunity.closedValue = closeData.value;
        updatedOpportunity.closedDate = closeData.date;
        if (newStage === DEAL_STAGES.CANCELLED) {
          updatedOpportunity.cancelledReason = closeData.reason;
        }
      }

      // First save the opportunity update
      await opportunityService.saveOpportunity(updatedOpportunity, currentUser.email);

      // Create the moment message with close value if applicable
      let momentMessage = `Deal stage changed from ${oldStage || 'none'} to ${newStage || 'none'}`;
      if (newStage === DEAL_STAGES.CLOSED_WON && closeData && closeData.value) {
        const formattedValue = formatCurrencyForDisplay(closeData.value);
        momentMessage += ` with closed value ${formattedValue || '$0'}`;
      }
      momentMessage += '.';

      // Then create the moment separately
      await opportunityService.createMoment(opportunityId, momentMessage, currentUser.email);

      // Get the updated opportunity with all its data
      const savedOpportunity = await opportunityService.getOpportunity(opportunityId);

      // Update the local state with the data returned from server
      setOpportunities(prevOpportunities =>
        prevOpportunities.map(opp =>
          opp.opportunityId === opportunityId ? savedOpportunity : opp
        )
      );

      setSuccess('Opportunity updated successfully');
    } catch (error) {
      console.error('Error updating opportunity stage:', error);
      setError('Failed to update opportunity stage');
      throw error;
    }
  };

  // Handle close dialog save
  const handleCloseDialogSave = async () => {
    await updateOpportunityStage(closeData.opportunityId, closeData.stage, closeData);
    setCloseDialogOpen(false);
  };

  // Toggle collapsed state of swim lanes
  const toggleLaneCollapse = (laneKey) => {
    setCollapsedLanes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(laneKey)) {
        newSet.delete(laneKey);
      } else {
        newSet.add(laneKey);
      }
      return newSet;
    });
  };

  // Group opportunities by swim lane (use all opportunities for swim lane, filtered for list)
  const groupedOpportunities = {
    BACKLOG: [],
    LEAD: [],
    QUALIFIED: [],
    PROPOSED: [],
    CLOSED: []
  };

  const opportunitiesToGroup = viewMode === 'swimlane' ? opportunities : filteredOpportunities;
  
  opportunitiesToGroup.forEach(opp => {
    const swimLane = getSwimLaneFromStage(opp.dealStage || 'BACKLOG');
    if (groupedOpportunities[swimLane]) {
      groupedOpportunities[swimLane].push(opp);
    } else {
      console.warn(`Swim lane "${swimLane}" not found in groupedOpportunities`);
    }
  });
  
  // Sort opportunities within each swim lane
  Object.keys(groupedOpportunities).forEach(lane => {
    groupedOpportunities[lane].sort((a, b) => {
      const getDateInET = (dateStr) => {
        if (!dateStr) return new Date(0);
        // Split off time part and use noon to avoid timezone issues
        const [datePart] = dateStr.split('T');
        const date = new Date(datePart + 'T12:00:00');
        return new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      };

      const dateA = CLOSED_STAGES.includes(a.dealStage) ? 
        getDateInET(a.closedDate) : getDateInET(a.estimatedCloseDate);
      const dateB = CLOSED_STAGES.includes(b.dealStage) ? 
        getDateInET(b.closedDate) : getDateInET(b.estimatedCloseDate);
      return dateB - dateA; // Descending order
    });
  });

  // Render opportunity card for swim lane view
  const renderOpportunityCard = (opportunity, index) => {
    // More flexible company lookup - try different ID formats
    const company = companies.find(c => 
      c.companyId === opportunity.companyId ||
      c.companyId === opportunity.companyId?.toLowerCase() ||
      c.companyId === opportunity.companyId?.toUpperCase() ||
      c.id === opportunity.companyId ||
      c.Id === opportunity.companyId
    );
    
    const isClosed = CLOSED_STAGES.includes(opportunity.dealStage);
    const value = isClosed ? opportunity.closedValue : opportunity.estimatedValue;
    const date = isClosed ? opportunity.closedDate : opportunity.estimatedCloseDate;
    // Hide value for cancelled deals and closed lost deals (since lost deals have no value)
    const showValue = opportunity.dealStage !== DEAL_STAGES.CANCELLED && 
                     opportunity.dealStage !== DEAL_STAGES.CLOSED_LOST;

    // Check if opportunity is overdue
    const isOverdue = !isClosed && date && new Date(date) < new Date();

    // Define card colors based on stage - muted colors with specific closed stage colors
    const currentCardStyle = {
      mb: 2,
      cursor: 'pointer',
      opacity: 1,
      background: (() => {
        // Orange background for lost or cancelled deals
        if (opportunity.dealStage === DEAL_STAGES.CLOSED_LOST || opportunity.dealStage === DEAL_STAGES.CANCELLED) {
          return 'linear-gradient(135deg, #fff3e0, #ffe0b2)';
        }
        // Red background for overdue deals
        if (isOverdue && !isClosed) {
          return 'linear-gradient(135deg, #fff5f5, #ffebee)';
        }
        // Default background
        return 'linear-gradient(135deg, #ffffff, #fafbfc)';
      })(),
      border: (() => {
        // Orange border for lost or cancelled deals
        if (opportunity.dealStage === DEAL_STAGES.CLOSED_LOST || opportunity.dealStage === DEAL_STAGES.CANCELLED) {
          return '1px solid #ff9800';
        }
        // Red border for overdue deals
        if (isOverdue && !isClosed) {
          return '1px solid #f44336';
        }
        // Default border
        return '1px solid #e0e0e0';
      })(),
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        borderColor: (() => {
          if (opportunity.dealStage === DEAL_STAGES.CLOSED_LOST || opportunity.dealStage === DEAL_STAGES.CANCELLED) {
            return '#f57c00';
          }
          if (isOverdue && !isClosed) {
            return '#d32f2f';
          }
          return '#bdbdbd';
        })(),
      }
    };

    return (
      <Draggable
        key={opportunity.opportunityId}
        draggableId={opportunity.opportunityId}
        index={index}
        type="OPPORTUNITY"
      >
        {renderDraggable((provided, snapshot) => (
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            sx={currentCardStyle}
            onClick={() => navigate(`/opportunities/${opportunity.opportunityId}`)}
          >
            <CardContent sx={{ pb: 1 }}>
              {/* Title */}
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {opportunity.opportunityName}
              </Typography>

              {/* Stage Badge for Lost/Cancelled Deals */}
              {(opportunity.dealStage === DEAL_STAGES.CLOSED_LOST || opportunity.dealStage === DEAL_STAGES.CANCELLED) && (
                <Box sx={{ 
                  display: 'inline-block',
                  bgcolor: '#ff9800',
                  color: 'white',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  mb: 1
                }}>
                  {opportunity.dealStage}
                </Box>
              )}

              {/* Company Link */}
              {company && (
                <Link
                  href={`/company/${company.companyId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  <BusinessIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                  {company.companyName}
                </Link>
              )}

              {/* Fallback: Show company name from opportunity data if company lookup failed */}
              {!company && opportunity.companyName && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <BusinessIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                  {opportunity.companyName}
                </Typography>
              )}

              {/* Show company ID if company not found for debugging */}
              {!company && !opportunity.companyName && opportunity.companyId && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontStyle: 'italic'
                  }}
                >
                  Company ID: {opportunity.companyId}
                </Typography>
              )}

              {/* Date and Value Row */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                bgcolor: `${currentCardStyle.borderColor}15`, // Same color as header but way lighter
                borderRadius: 1,
                p: 1
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    mr: 2
                  }}
                >
                  {formatDate(date)}
                </Typography>
                {showValue && value && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'bold', // Make value bold
                      color: currentCardStyle.borderColor // Same color as card header
                    }}
                  >
                    {formatCurrency(value)}
                  </Typography>
                )}
              </Box>

              {/* Salesperson Row */}
              {opportunity.salesPersonEmails && opportunity.salesPersonEmails.length > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  bgcolor: `${currentCardStyle.borderColor}08`,
                  borderRadius: 1,
                  p: 1
                }}>
                  
                  <SalespersonDisplay opportunity={opportunity} getCachedUserData={getCachedUserData} />
                </Box>
              )}

              {/* Action Links */}
              <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/opportunities/${opportunity.opportunityId}`);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  <EditIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                  Edit
                </Link>
                
                {/* Delete button for admin users */}
                {isAdmin && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(opportunity);
                    }}
                    title="Delete Opportunity"
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Draggable>
    );
  };

  const handleDeleteClick = (opportunity) => {
    setOpportunityToDelete(opportunity);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setOpportunityToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setErrorMessage('');
  };

  const handleDeleteConfirm = async () => {
    if (!opportunityToDelete) return;

    setDeleting(true);
    try {
      // First check if the opportunity can be deleted
      const deletionCheck = await opportunityService.checkOpportunityDeletion(opportunityToDelete.opportunityId);
      
      if (!deletionCheck.canDelete) {
        // Show error with talent details
        const talentLinks = deletionCheck.talentDetails.map(talent => 
          `${talent.name} (${talent.id})`
        ).join(', ');
        
        const errorMessage = `Cannot delete opportunity "${opportunityToDelete.opportunityName}". It has ${deletionCheck.jobHistoryCount} job history record(s) associated with the following talents: ${talentLinks}`;
        
        setErrorMessage(errorMessage);
        setErrorDialogOpen(true);
        handleDeleteCancel();
        return;
      }
      
      // Proceed with deletion
      await opportunityService.deleteOpportunity(opportunityToDelete.opportunityId);
      setOpportunities(opportunities.filter(o => o.opportunityId !== opportunityToDelete.opportunityId));
      handleDeleteCancel();
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      
      if (error.message === 'OPPORTUNITY_HAS_JOB_HISTORY') {
        // This should not happen since we check above, but handle just in case
        setErrorMessage('Cannot delete opportunity due to associated job history');
        setErrorDialogOpen(true);
        handleDeleteCancel();
      } else {
        setError('Failed to delete opportunity. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  // Simple salesperson display component
  const SalespersonDisplay = ({ opportunity, getCachedUserData }) => {
    // Handle both PascalCase and camelCase field names
    const salesPersonEmails = opportunity.salesPersonEmails || opportunity.SalesPersonEmails;
    const firstSalespersonEmail = opportunity.FirstSalespersonEmail || opportunity.firstSalespersonEmail;
    const firstSalespersonName = opportunity.FirstSalespersonName || opportunity.firstSalespersonName;
    const firstSalespersonInitials = opportunity.FirstSalespersonInitials || opportunity.firstSalespersonInitials;

    // Use FirstSalespersonEmail directly if salesPersonEmails is not available
    const email = firstSalespersonEmail || (salesPersonEmails && salesPersonEmails.length > 0 ? salesPersonEmails[0] : null);
    
    if (!email) {
      return null;
    }

    // Let UserDisplay component use the centralized cache directly
    return (
      <UserDisplay 
        email={email}
        size="small"
        showName={true}
      />
    );
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2,
          p: 3
        }}
      >
        <CircularProgress 
          size={60} 
          thickness={4}
          sx={{
            color: 'primary.main',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }}
        />
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            fontWeight: 500,
            textAlign: 'center'
          }}
        >
          Loading opportunities...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
              <OpportunitiesIcon sx={{ mr: 2 }} />
              Opportunities
            </Typography>

            {/* refreshing && (
              <Typography variant="body2" color="text.secondary">
                Refreshing data...
              </Typography>
            ) */}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Refresh Button */}
            <IconButton
              onClick={() => loadData(true)}
              disabled={loading || refreshing}
              title="Refresh data"
              sx={{ 
                color: 'primary.main',
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
            
            {/* View Mode Toggle - Hide on mobile */}
            {!isMobile && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newView) => newView && setViewMode(newView)}
                aria-label="view mode"
              >
                <ToggleButton value="swimlane">
                  <ViewColumnIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            
            {/* Add Button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/opportunities/new')}
            >
              Add Opportunity
            </Button>
          </Box>
        </Box>

        {/* Filters - Only show in list view */}
        {viewMode === 'list' && (
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              {/* Search Box */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search opportunities..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchTerm('');
                            setPage(0);
                          }}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  placeholder="Search by name, company, or LOB"
                />
              </Grid>

              {/* Stage Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Stages</InputLabel>
                  <Select
                    multiple
                    value={selectedStages}
                    onChange={handleStageFilterChange}
                    input={<OutlinedInput label="Filter by Stages" />}
                    renderValue={(selected) => 
                      selected.length === Object.values(DEAL_STAGES).length 
                        ? 'All Stages' 
                        : `${selected.length} stages selected`
                    }
                  >
                    <MenuItem value="All">
                      <Checkbox 
                        checked={selectedStages.length === Object.values(DEAL_STAGES).length}
                        indeterminate={selectedStages.length > 0 && selectedStages.length < Object.values(DEAL_STAGES).length}
                      />
                      <ListItemText primary="All" />
                    </MenuItem>
                    {Object.values(DEAL_STAGES).map((stage) => (
                      <MenuItem key={stage} value={stage}>
                        <Checkbox checked={selectedStages.includes(stage)} />
                        <ListItemText primary={stage} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* LOB Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by LOB</InputLabel>
                  <Select
                    multiple
                    value={selectedLOBs}
                    onChange={handleLOBFilterChange}
                    input={<OutlinedInput label="Filter by LOB" />}
                    renderValue={(selected) => 
                      selected.length === 0 
                        ? 'No LOBs selected'
                        : selected.length === lobOptions.length 
                          ? 'All LOBs' 
                          : `${selected.length} LOBs selected`
                    }
                  >
                    <MenuItem value="All">
                      <Checkbox 
                        checked={selectedLOBs.length === lobOptions.length}
                        indeterminate={selectedLOBs.length > 0 && selectedLOBs.length < lobOptions.length}
                      />
                      <ListItemText primary="All" secondary={selectedLOBs.length === lobOptions.length ? "Click to clear all" : "Click to select all"} />
                    </MenuItem>
                    {lobOptions.map((lob) => (
                      <MenuItem key={lob.Id || lob.id} value={lob.Name || lob.name}>
                        <Checkbox checked={selectedLOBs.includes(lob.Name || lob.name)} />
                        <ListItemText primary={lob.Name || lob.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Time Range Filter - Move to new row */}
              <Grid item xs={12} md={6}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={timeRangeEnabled}
                        onChange={handleTimeRangeToggle}
                        color="primary"
                      />
                    }
                    label="Date Range Filter"
                    sx={{ mb: 1 }}
                  />
                  {timeRangeEnabled && (
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={handleEndDateChange}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Content based on view mode */}
        {viewMode === 'swimlane' && !isMobile ? (
          // Swim Lane View
          <DragDropContext onDragEnd={handleDragEnd}>
            <Grid container spacing={2} sx={{ 
              height: {
                xs: 'calc(100vh - 280px)', // Mobile: account for all elements
                sm: 'calc(100vh - 260px)', // Tablet: better space usage
                md: 'calc(100vh - 240px)', // Desktop: maximize space
                lg: 'calc(100vh - 220px)', // Large desktop: more space
                xl: 'calc(100vh - 200px)'  // Extra large: maximum utilization
              },
              position: 'relative',
              zIndex: 1
            }}>
              {Object.entries(SWIM_LANES).map(([key, title]) => {
                const isCollapsed = collapsedLanes.has(key);
                const isCollapsible = key === 'BACKLOG';
                
                // Calculate dynamic width based on collapse state
                const totalLanes = Object.keys(SWIM_LANES).length;
                const numCollapsedLanes = collapsedLanes.size; // Use Set size instead of filtering
                const expandedLanes = totalLanes - numCollapsedLanes;
                
                // When collapsed: 1 unit for collapsed, remaining space divided among expanded
                // When all expanded: 12/5 = 2.4 units each
                const collapsedWidth = 1;
                const expandedWidth = expandedLanes > 0 ? (12 - (numCollapsedLanes * collapsedWidth)) / expandedLanes : 12;
                
                const gridWidth = isCollapsed ? collapsedWidth : expandedWidth;
                
                // Define colors and styling for each swim lane - muted colors
                const laneStyles = {
                  BACKLOG: {
                    gradient: 'linear-gradient(135deg, #6c7b95 0%, #8a92b2 100%)',
                    headerColor: '#6c7b95',
                    shadowColor: 'rgba(108, 123, 149, 0.2)',
                    chipColor: '#6c7b95'
                  },
                  LEAD: {
                    gradient: 'linear-gradient(135deg, #9b8ce8 0%, #a8a3d7 100%)',
                    headerColor: '#9b8ce8',
                    shadowColor: 'rgba(155, 140, 232, 0.2)',
                    chipColor: '#9b8ce8'
                  },
                  QUALIFIED: {
                    gradient: 'linear-gradient(135deg, #7ba7d7 0%, #85b3d9 100%)',
                    headerColor: '#7ba7d7',
                    shadowColor: 'rgba(123, 167, 215, 0.2)',
                    chipColor: '#7ba7d7'
                  },
                  PROPOSED: {
                    gradient: 'linear-gradient(135deg, #8db3a3 0%, #9fc0b2 100%)',
                    headerColor: '#8db3a3',
                    shadowColor: 'rgba(141, 179, 163, 0.2)',
                    chipColor: '#8db3a3'
                  },
                  CLOSED: {
                    gradient: 'linear-gradient(135deg, #b8a693 0%, #c9b8a8 100%)',
                    headerColor: '#b8a693',
                    shadowColor: 'rgba(184, 166, 147, 0.2)',
                    chipColor: '#b8a693'
                  }
                };
                
                const currentStyle = laneStyles[key];
                
                return (
                  <Grid item xs={12} md={gridWidth} key={key}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        height: '100%',
                        display: 'flex', 
                        flexDirection: 'column',
                        minWidth: isCollapsed ? '80px' : 'auto',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: `0 8px 32px ${currentStyle.shadowColor}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 40px ${currentStyle.shadowColor}`,
                        }
                      }}
                    >
                      {/* Header Section with Gradient */}
                      <Box 
                        sx={{ 
                          background: currentStyle.gradient,
                          p: 2,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '1px',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isCollapsible && (
                            <IconButton
                              size="small"
                              onClick={() => toggleLaneCollapse(key)}
                              sx={{ 
                                mr: isCollapsed ? 0 : 1,
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.3)',
                                  transform: 'scale(1.1)',
                                }
                              }}
                            >
                              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                            </IconButton>
                          )}
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: 'white',
                              fontWeight: 600,
                              textAlign: 'center',
                              writingMode: isCollapsed ? 'vertical-lr' : 'horizontal-tb',
                              textOrientation: isCollapsed ? 'mixed' : 'unset',
                              fontSize: isCollapsed ? '0.9rem' : '1.1rem',
                              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {title}
                          </Typography>
                          <Chip 
                            label={groupedOpportunities[key].length} 
                            size="small" 
                            sx={{ 
                              ml: isCollapsed ? 0 : 1,
                              mt: isCollapsed ? 1 : 0,
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              color: currentStyle.chipColor,
                              fontWeight: 600,
                              fontSize: isCollapsed ? '0.7rem' : '0.75rem',
                              backdropFilter: 'blur(10px)',
                              border: 'none',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }} 
                          />
                        </Box>
                      </Box>
                      
                      {/* Content Area */}
                      <Box sx={{ flexGrow: 1, position: 'relative' }}>
                        <Droppable droppableId={key} type="OPPORTUNITY">
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              sx={{
                                height: {
                                  xs: 'calc(100vh - 360px)', // Mobile: account for all elements
                                  sm: 'calc(100vh - 340px)', // Tablet: better space usage
                                  md: 'calc(100vh - 320px)', // Desktop: maximize space
                                  lg: 'calc(100vh - 300px)', // Large desktop: more space
                                  xl: 'calc(100vh - 280px)'  // Extra large: maximum utilization
                                },
                                overflow: 'auto',
                                background: snapshot.isDraggingOver 
                                  ? `linear-gradient(135deg, ${currentStyle.headerColor}15, ${currentStyle.headerColor}25)`
                                  : 'transparent',
                                borderRadius: '0 0 16px 16px',
                                p: 2,
                                transition: 'all 0.3s ease',
                                minHeight: {
                                  xs: 'calc(100vh - 360px)', // Mobile: account for all elements
                                  sm: 'calc(100vh - 340px)', // Tablet: better space usage
                                  md: 'calc(100vh - 320px)', // Desktop: maximize space
                                  lg: 'calc(100vh - 300px)', // Large desktop: more space
                                  xl: 'calc(100vh - 280px)'  // Extra large: maximum utilization
                                },
                                maxHeight: {
                                  xs: 'calc(100vh - 360px)', // Mobile: account for all elements
                                  sm: 'calc(100vh - 340px)', // Tablet: better space usage
                                  md: 'calc(100vh - 320px)', // Desktop: maximize space
                                  lg: 'calc(100vh - 300px)', // Large desktop: more space
                                  xl: 'calc(100vh - 280px)'  // Extra large: maximum utilization
                                },
                                // Custom scrollbar
                                '&::-webkit-scrollbar': {
                                  width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                  background: 'rgba(0,0,0,0.05)',
                                  borderRadius: '4px',
                                  margin: '4px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  background: currentStyle.headerColor,
                                  borderRadius: '4px',
                                  opacity: 0.7,
                                  '&:hover': {
                                    opacity: 1,
                                  }
                                },
                                // Firefox scrollbar
                                scrollbarWidth: 'thin',
                                scrollbarColor: `${currentStyle.headerColor} rgba(0,0,0,0.05)`,
                              }}
                            >
                              {isCollapsed ? (
                                // Collapsed view
                                <Box
                                  sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {groupedOpportunities[key].length > 0 && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        writingMode: 'vertical-lr',
                                        textOrientation: 'mixed',
                                        color: 'text.secondary',
                                        fontStyle: 'italic',
                                        opacity: 0.7
                                      }}
                                    >
                                      Click to expand
                                    </Typography>
                                  )}
                                  {provided.placeholder}
                                </Box>
                              ) : (
                                // Expanded view
                                <>
                                  {groupedOpportunities[key].map((opportunity, index) =>
                                    renderOpportunityCard(opportunity, index)
                                  )}
                                  {provided.placeholder}
                                  
                                  {/* Empty state */}
                                  {groupedOpportunities[key].length === 0 && (
                                    <Box
                                      sx={{
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0.5
                                      }}
                                    >
                                      <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        sx={{ fontStyle: 'italic' }}
                                      >
                                        Drop opportunities here
                                      </Typography>
                                    </Box>
                                  )}
                                </>
                              )}
                            </Box>
                          )}
                        </Droppable>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </DragDropContext>
        ) : (
          // List View
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {renderSortableHeader('name', 'Opportunity Name')}
                  {renderSortableHeader('company', 'Company')}
                  {!isMobile && renderSortableHeader('value', 'Value')}
                  {renderSortableHeader('date', 'Date')}
                  <TableCell>Sales Person</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOpportunities
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((opportunity, index) => {
                  // More flexible company lookup - try different ID formats
                  const company = companies.find(c => 
                    c.companyId === opportunity.companyId ||
                    c.companyId === opportunity.companyId?.toLowerCase() ||
                    c.companyId === opportunity.companyId?.toUpperCase() ||
                    c.id === opportunity.companyId ||
                    c.Id === opportunity.companyId
                  );
                  
                  const firstSalesPerson = getFirstSalesPerson(opportunity.salesPersonEmails);
                  const isClosed = CLOSED_STAGES.includes(opportunity.dealStage);
                  const value = isClosed ? opportunity.closedValue : opportunity.estimatedValue;
                  const date = isClosed ? opportunity.closedDate : opportunity.estimatedCloseDate;
                  // Hide value for cancelled deals and closed lost deals (since lost deals have no value)
                  const showValue = opportunity.dealStage !== DEAL_STAGES.CANCELLED && 
                                   opportunity.dealStage !== DEAL_STAGES.CLOSED_LOST;

                  return (
                    <TableRow key={opportunity.opportunityId} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {opportunity.opportunityName}
                          </Typography>
                          <Chip 
                            label={opportunity.dealStage} 
                            size="small" 
                            variant="outlined"
                            color={CLOSED_STAGES.includes(opportunity.dealStage) ? 'default' : 'primary'}
                          />
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        {company ? (
                          <Typography 
                            variant="body2" 
                            color="primary" 
                            sx={{ 
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                            onClick={() => navigate(`/company/${company.companyId}`)}
                          >
                            {company.companyName}
                          </Typography>
                        ) : opportunity.companyName ? (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                          >
                            {opportunity.companyName}
                          </Typography>
                        ) : opportunity.companyId ? (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}
                          >
                            ID: {opportunity.companyId}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      
                      {!isMobile && (
                        <TableCell>
                          {showValue ? formatCurrency(value) : '-'}
                        </TableCell>
                      )}
                      
                      <TableCell>
                        {formatDate(date)}
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SalespersonDisplay opportunity={opportunity} getCachedUserData={getCachedUserData} />
                        </Box>
                      </TableCell>
                      

                      
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/opportunities/${opportunity.opportunityId}`)}
                            title="Edit Opportunity"
                          >
                            <EditIcon />
                          </IconButton>
                          {/* Delete button only for admin users */}
                          {isAdmin && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(opportunity)}
                              title="Delete Opportunity"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredOpportunities.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 5 : 5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {opportunities.length === 0 
                          ? 'No opportunities found. Create your first opportunity to get started.'
                          : 'No opportunities match your selected stages.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            {filteredOpportunities.length > 0 && (
              <TablePagination
                component="div"
                count={filteredOpportunities.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            )}
          </TableContainer>
        )}



        {/* Close Dialog */}
        <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Close Opportunity</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Closed Stage</InputLabel>
                  <Select
                    value={closeData.stage}
                    onChange={(e) => {
                      const selectedStage = e.target.value;
                      let newValue = closeData.value;
                      
                      // Auto-populate value based on selected stage
                      if (selectedStage === DEAL_STAGES.CLOSED_WON) {
                        // Use current estimated value for won deals
                        const opportunity = opportunities.find(opp => opp.opportunityId === closeData.opportunityId);
                        newValue = opportunity ? (opportunity.estimatedValue || 0) : 0;
                      } else if (selectedStage === DEAL_STAGES.CLOSED_LOST) {
                        // Use $0 for lost deals
                        newValue = 0;
                      }
                      
                      setCloseData(prev => ({ 
                        ...prev, 
                        stage: selectedStage,
                        value: newValue
                      }));
                    }}
                    label="Closed Stage"
                  >
                    {CLOSED_STAGES.map(stage => (
                      <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {closeData.stage === DEAL_STAGES.CANCELLED ? (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cancelled Reason"
                    multiline
                    rows={3}
                    value={closeData.reason}
                    onChange={(e) => setCloseData(prev => ({ ...prev, reason: e.target.value }))}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Closed Value"
                    type="text"
                    value={formatCurrencyForDisplay(closeData.value)}
                    onChange={(e) => setCloseData(prev => ({ ...prev, value: parseCurrencyInput(e.target.value) }))}
                    placeholder="$0"
                  />
                </Grid>
              )}
              
                             <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Closed Date"
                  type="date"
                  value={closeData.date ? closeData.date.toISOString().split('T')[0] : ''}
                  onChange={(e) => setCloseData(prev => ({ ...prev, date: e.target.value ? new Date(e.target.value) : new Date() }))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCloseDialogSave} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Delete Opportunity</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the opportunity "{opportunityToDelete?.opportunityName}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error Dialog for Job History */}
        <Dialog open={errorDialogOpen} onClose={handleErrorDialogClose}>
          <DialogTitle>Cannot Delete Opportunity</DialogTitle>
          <DialogContent>
            <Typography color="error">
              {errorMessage}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleErrorDialogClose} variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbars */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
        >
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
        >
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default Opportunities;
